import React, { useState, useEffect } from 'react';
import { createAdmin, getAdmins, updateAdmin, deleteAdmin, toggleAdminStatus } from '../../services/adminService';
import { useAuth } from '../../context/AuthContext';

const INIT = { name: '', email: '', password: '' };

const CreateAdmin = () => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState(INIT);
  const [admins, setAdmins] = useState([]);
  const [msg, setMsg] = useState({ text: '', type: '' });
  const [editMode, setEditMode] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState(null);

  const fetchAdmins = async () => {
    try { setAdmins(await getAdmins()); } catch { /* silent */ }
  };

  useEffect(() => { fetchAdmins(); }, []);

  useEffect(() => {
    if (msg.text) {
      const t = setTimeout(() => setMsg({ text: '', type: '' }), 3000);
      return () => clearTimeout(t);
    }
  }, [msg.text]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(f => ({ ...f, [name]: value }));
  };

  const cancelEdit = () => { setFormData(INIT); setEditMode(false); setEditingAdminId(null); };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      if (editMode) {
        const payload = {};
        if (formData.name.trim()) payload.name = formData.name.trim();
        if (formData.email.trim()) payload.email = formData.email.trim();
        if (formData.password.trim()) payload.password = formData.password.trim();
        await updateAdmin(editingAdminId, payload);
        setMsg({ text: 'Admin updated successfully.', type: 'success' });
      } else {
        if (!formData.name || !formData.email || !formData.password) {
          setMsg({ text: 'All fields are required.', type: 'error' }); return;
        }
        await createAdmin({ name: formData.name.trim(), email: formData.email.trim(), password: formData.password.trim() });
        setMsg({ text: 'Admin created successfully.', type: 'success' });
      }
      cancelEdit(); fetchAdmins();
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Operation failed.', type: 'error' });
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this admin?')) return;
    try {
      await deleteAdmin(id);
      setMsg({ text: 'Admin deleted.', type: 'success' });
      fetchAdmins();
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Delete failed.', type: 'error' });
    }
  };

  const handleToggleStatus = async (id, currentStatus) => {
    try {
      await toggleAdminStatus(id, !currentStatus);
      setMsg({ text: `Admin ${!currentStatus ? 'activated' : 'deactivated'} successfully.`, type: 'success' });
      fetchAdmins();
    } catch (err) {
      setMsg({ text: err.response?.data?.message || 'Failed to update status.', type: 'error' });
    }
  };

  const handleEdit = admin => {
    setFormData({ name: admin.name, email: admin.email, password: '' });
    setEditMode(true); setEditingAdminId(admin._id);
    window.scrollTo(0, 0);
  };

  if (!currentUser?.isSuperAdmin) {
    return (
      <div className="alert alert-error">
        Unauthorized: Only Super Admin can access this page.
      </div>
    );
  }

  return (
    <div className="create-admin-page">
      <div className="ph">
        <div><h1>{editMode ? 'Edit Admin' : 'Create Admin'}</h1><p>Manage admin accounts</p></div>
      </div>

      {msg.text && (
        <div className={`alert alert-${msg.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: 14 }}>
          {msg.text}
          <button className="alert-close" onClick={() => setMsg({ text: '', type: '' })}>✕</button>
        </div>
      )}

      {/* Form */}
      <div className="admin-form-card">
        <div className="admin-form-title">{editMode ? 'Update Admin' : 'New Admin'}</div>
        <form onSubmit={handleSubmit}>
          <div className="admin-form-grid">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input className="form-control" name="name" value={formData.name} onChange={handleChange} placeholder="Full name" required={!editMode} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-control" type="email" name="email" value={formData.email} onChange={handleChange} placeholder="admin@example.com" required={!editMode} />
            </div>
            <div className="form-group">
              <label className="form-label">{editMode ? 'New Password (leave blank to keep)' : 'Password'}</label>
              <input className="form-control" type="password" name="password" value={formData.password} onChange={handleChange} placeholder="••••••••" required={!editMode} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button type="submit" className="btn btn-primary btn-sm">
              <i className={`fas fa-${editMode ? 'save' : 'plus'}`} /> {editMode ? 'Update' : 'Create Admin'}
            </button>
            {editMode && (
              <button type="button" className="btn btn-outline btn-sm" onClick={cancelEdit}>Cancel</button>
            )}
          </div>
        </form>
      </div>

      {/* Admins table */}
      <div className="tbl-wrap">
        {admins.length === 0 ? (
          <div className="empty-state"><i className="fas fa-user-shield" style={{ fontSize: 32, color: 'var(--t3)' }} /><p>No admins found</p></div>
        ) : (
          <table className="tbl">
            <thead>
              <tr><th>#</th><th>Name</th><th>Email</th><th>Status</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {admins.map((a, i) => (
                <tr key={a._id}>
                  <td className="col-num">{i + 1}</td>
                  <td style={{ fontWeight: 500 }}>{a.name}</td>
                  <td style={{ color: 'var(--t2)', fontSize: 12 }}>{a.email}</td>
                  <td>
                    <span className={`store-badge ${a.isActive !== false ? 'store-open' : 'store-closed'}`}>
                      <span className="store-dot" />
                      {a.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => handleToggleStatus(a._id, a.isActive !== false)}>
                        {a.isActive !== false ? 'Deactivate' : 'Activate'}
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => handleEdit(a)}>
                        <i className="fas fa-edit" /> Edit
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a._id)}>
                        <i className="fas fa-trash" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default CreateAdmin;
